import React from 'react';

interface Message {
  id: string;
  content: string;
  direction: 'INBOUND' | 'OUTBOUND';
  timestamp: string;
  status?: 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
  messageType: 'TEXT' | 'IMAGE' | 'DOCUMENT' | 'AUDIO' | 'VIDEO' | 'TEMPLATE';
  mediaUrl?: string;
  metadata?: string;
  sender?: {
    name: string;
  };
}

interface MessageBubbleProps {
  message: Message;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isOutgoing = message.direction === 'OUTBOUND';
  
  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getStatusIcon = () => {
    if (!isOutgoing) return null;
    
    switch (message.status) {
      case 'SENT': return '✓';
      case 'DELIVERED': return '✓✓';
      case 'READ': return <span style={{ color: '#60a5fa' }}>✓✓</span>;
      case 'FAILED': return '❌';
      default: return null;
    }
  };

  const renderContent = () => {
    if (message.messageType === 'TEXT') {
      return <div className="message-content">{message.content}</div>;
    }
    
    if (message.messageType === 'TEMPLATE') {
      return (
        <div className="message-content">
          {message.content}
        </div>
      );
    }
    
    if (message.messageType === 'IMAGE' && message.mediaUrl) {
      return (
        <div className="message-content media-content">
          <img 
            src={message.mediaUrl} 
            alt="Attachment" 
            className="message-image"
            style={{ 
              maxWidth: '300px', 
              maxHeight: '300px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'block'
            }}
            onClick={() => window.open(message.mediaUrl, '_blank')}
          />
          {message.content && <div style={{ marginTop: '8px' }}>{message.content}</div>}
        </div>
      );
    }
    
    if (message.messageType === 'VIDEO' && message.mediaUrl) {
      return (
        <div className="message-content media-content">
          <video 
            controls 
            style={{ 
              maxWidth: '300px', 
              maxHeight: '300px',
              borderRadius: '8px'
            }}
          >
            <source src={message.mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          {message.content && <div style={{ marginTop: '8px' }}>{message.content}</div>}
        </div>
      );
    }
    
    if (message.messageType === 'AUDIO' && message.mediaUrl) {
      return (
        <div className="message-content media-content">
          <audio controls style={{ maxWidth: '300px' }}>
            <source src={message.mediaUrl} type="audio/mpeg" />
            Your browser does not support the audio tag.
          </audio>
          {message.content && <div style={{ marginTop: '8px' }}>{message.content}</div>}
        </div>
      );
    }
    
    if (message.messageType === 'DOCUMENT' && message.mediaUrl) {
      const fileName = message.content || 'Document';
      return (
        <div className="message-content media-content">
          <a 
            href={message.mediaUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              backgroundColor: isOutgoing ? 'rgba(255,255,255,0.1)' : '#f3f4f6',
              borderRadius: '8px',
              textDecoration: 'none',
              color: 'inherit'
            }}
          >
            <span style={{ fontSize: '24px' }}>📄</span>
            <div>
              <div style={{ fontWeight: '500' }}>{fileName}</div>
              <div style={{ fontSize: '12px', opacity: 0.7 }}>Click to download</div>
            </div>
          </a>
        </div>
      );
    }
    
    return (
      <div className="message-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>📎</span>
          <span>{message.messageType.toLowerCase()} file</span>
        </div>
        {message.content && <div style={{ marginTop: '8px' }}>{message.content}</div>}
      </div>
    );
  };

  return (
    <div className={`message-bubble ${isOutgoing ? 'outgoing' : 'incoming'}`}>
      {renderContent()}
      <div className="message-meta">
        <span>{formatTime(message.timestamp)}</span>
        {getStatusIcon()}
      </div>
    </div>
  );
};

export default MessageBubble;
